import { motion } from 'framer-motion';
import { useEffect } from 'react';
import { BarChart3, Users, TrendingUp, Building2, GraduationCap } from 'lucide-react';

const TALLY_FORM_ID = "1AbMbO";

// Logos for credibility
const LOGOS = {
  stanford: "https://identity.stanford.edu/wp-content/uploads/sites/3/2020/07/su-stacked.png",
};

export default function ReportPage() {
  useEffect(() => {
    // Load Tally embed script
    const script = document.createElement('script');
    script.src = 'https://tally.so/widgets/embed.js';
    script.async = true;
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  const benefits = [
    {
      icon: BarChart3,
      title: "Industry Benchmarks",
      description: "See how your L&D strategy compares to peers across industries"
    },
    {
      icon: TrendingUp,
      title: "AI Readiness Score",
      description: "Understand where your organization stands in the AI-era learning curve"
    },
    {
      icon: Building2,
      title: "Strategic Insights",
      description: "Get early access to the full report with actionable recommendations"
    }
  ];

  return (
    <main data-testid="report-page" className="bg-[#0f151c] min-h-screen">
      {/* Hero Section with Credibility */}
      <section className="pt-24 lg:pt-32 pb-8 lg:pb-12">
        <div className="container-peak px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl mx-auto text-center"
          >
            {/* Credibility Badge */}
            <div className="inline-flex items-center gap-3 px-5 py-3 bg-[#1a2330] border border-[#e8dcc7]/20 rounded-full mb-8">
              <BarChart3 className="w-5 h-5 text-[#e17c18]" />
              <div className="text-left">
                <p className="text-[#e8dcc7] text-sm font-medium">Peak 9 Industry Research 2026</p>
              </div>
            </div>
            
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-light text-[#e8dcc7] leading-tight mb-6">
              The State of Leadership Learning in the{' '}
              <span className="text-[#e17c18]">AI Era</span>
            </h1>
            
            <p className="text-[#e8dcc7]/70 text-base lg:text-lg mb-6 leading-relaxed">
              How are organizations adapting their leadership development strategies? 
              We're building the definitive picture — and we need your perspective.
            </p>

            <div className="flex flex-wrap justify-center gap-2 text-[#e8dcc7]/50 text-xs font-['Syne'] uppercase tracking-wider mb-8">
              <span className="px-3 py-1 border border-[#e8dcc7]/20 rounded-full">CHROs</span>
              <span className="px-3 py-1 border border-[#e8dcc7]/20 rounded-full">CLOs</span>
              <span className="px-3 py-1 border border-[#e8dcc7]/20 rounded-full">Head of People</span>
              <span className="px-3 py-1 border border-[#e8dcc7]/20 rounded-full">L&D Leaders</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* About the Research - Trust Building */}
      <section className="py-8 lg:py-12 border-y border-[#e8dcc7]/10">
        <div className="container-peak px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="max-w-3xl mx-auto"
          >
            <div className="flex flex-col md:flex-row items-start gap-6 md:gap-8">
              <div className="shrink-0">
                <div className="w-16 h-16 rounded-full bg-[#e17c18]/10 flex items-center justify-center">
                  <GraduationCap className="w-8 h-8 text-[#e17c18]" />
                </div>
              </div>
              <div>
                <h2 className="text-lg lg:text-xl text-[#e8dcc7] font-medium mb-3">
                  Why This Research Matters
                </h2>
                <p className="text-[#e8dcc7]/70 text-sm leading-relaxed mb-4">
                  This research is led by <span className="text-[#e8dcc7]">Shayamal Vallabhjee</span>, Human Performance Designer 
                  who also teaches at <span className="text-[#e8dcc7]">Stanford Graduate School of Business</span>. 
                  His work focuses on understanding how AI is fundamentally reshaping leadership development.
                </p>
                <p className="text-[#e8dcc7]/60 text-sm leading-relaxed">
                  After 25 years working with Olympians, Fortune 500 leaders, and high-performing teams, 
                  Peak 9 is now mapping how the world's best organizations are adapting their learning strategies for the AI era.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Embedded Form Section */}
      <section className="py-12 lg:py-16">
        <div className="container-peak px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="max-w-2xl mx-auto"
          >
            <div className="text-center mb-8">
              <h2 className="text-xl lg:text-2xl font-light text-[#e8dcc7] mb-2">
                Participate in the Research
              </h2>
              <p className="text-[#e8dcc7]/60 text-sm">
                5 minutes. Your responses are confidential.
              </p>
            </div>

            {/* Tally Embedded Form */}
            <div className="bg-white rounded-lg overflow-hidden">
              <iframe
                data-tally-src={`https://tally.so/embed/${TALLY_FORM_ID}?alignLeft=1&hideTitle=1&transparentBackground=1&dynamicHeight=1`}
                loading="lazy"
                width="100%"
                height="500"
                frameBorder="0"
                marginHeight="0"
                marginWidth="0"
                title="Leadership Learning Survey"
                style={{ minHeight: '500px' }}
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* What You'll Get - Below Form */}
      <section className="py-12 lg:py-16 bg-[#1a2330]/30">
        <div className="container-peak px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="max-w-4xl mx-auto"
          >
            <h3 className="text-center text-[#e8dcc7]/60 text-xs font-['Syne'] uppercase tracking-[0.2em] mb-8">
              What You'll Receive When The Report Is Ready
            </h3>
            
            <div className="grid sm:grid-cols-3 gap-6">
              {benefits.map((benefit, i) => (
                <div key={i} className="text-center p-6 border border-[#e8dcc7]/10 bg-[#0f151c]/50">
                  <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-[#e17c18]/10 flex items-center justify-center">
                    <benefit.icon className="w-6 h-6 text-[#e17c18]" />
                  </div>
                  <h3 className="text-[#e8dcc7] font-medium mb-2">{benefit.title}</h3>
                  <p className="text-[#e8dcc7]/60 text-sm">{benefit.description}</p>
                </div>
              ))}
            </div>

            <p className="text-center text-[#e8dcc7]/40 text-xs mt-8">
              Results will be published in aggregate only. Individual responses remain confidential.
            </p>
          </motion.div>
        </div>
      </section>
    </main>
  );
}
